import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import { Link } from "react-router";
import { Avatar, Checkbox, Icon, Pill } from "@astrum/ui";
import type { PillTone } from "@astrum/ui";
import { CUSTOM_COLUMN, TABLE_COLUMN_IDS, customColumnId } from "@astrum/shared";
import type { CustomField, TableColumnId, ViewColumnId } from "@astrum/shared";
import { api } from "../../lib/api.js";
import type { Task } from "../../lib/api.js";
import { dayKey, dayOf } from "../../lib/work-views.js";
import {
  COLUMN_LABELS,
  DEFAULT_COLUMN_WIDTHS,
  VIRTUALISE_AFTER,
  clampWidth,
  nextSort,
  sortTasks,
  virtualWindow,
} from "../../lib/work-views.js";
import { formatCustomValue, parseNumberInput } from "../../lib/custom-fields.js";
import { TagChip } from "../TagPicker.js";
import type { TaskViewProps } from "./types.js";
import "./task-views.css";

const ROW_HEIGHT = 38;

const STATUSES: { value: Task["status"]; label: string }[] = [
  { value: "todo", label: "To do" },
  { value: "in_progress", label: "In progress" },
  { value: "review", label: "Review" },
  { value: "done", label: "Done" },
  { value: "cancelled", label: "Cancelled" },
];
const PRIORITIES: Task["priority"][] = ["low", "normal", "medium", "high", "urgent"];
const PRIORITY_TONE: Record<Task["priority"], PillTone> = {
  low: "neutral",
  normal: "neutral",
  medium: "warn",
  high: "danger",
  urgent: "danger",
};

type Person = { id: string; displayName: string };

const dateValue = (iso: string | null) => (iso ? dayKey(dayOf(iso)) : "");
const dateIso = (value: string) => (value ? `${value}T00:00:00.000Z` : null);

/**
 * The table view: one task per row, a spreadsheet's worth of columns.
 *
 * Sorting happens here, on the tasks already loaded, so it is instant and works
 * on a saved list too. Columns can be shown, hidden and resized; all of that
 * lives in the page's view state (and so the URL and saved views), not here.
 *
 * Past VIRTUALISE_AFTER rows only the rows in (and just outside) the scroll
 * window are mounted. Row height is fixed so the window is arithmetic, not
 * measurement.
 */
export function TableView({ tasks, canEdit, onPatch, state, onState, customFields = [], onCustomValue }: TaskViewProps) {
  /* A `cf:` column whose field is gone (deleted, or another project's saved view) is dropped, not shown empty. */
  const fieldById = useMemo(() => new Map(customFields.map((f) => [f.id, f])), [customFields]);
  const columns = useMemo(
    () => state.columns.filter((id) => !CUSTOM_COLUMN.test(id) || fieldById.has(id.slice(3))),
    [state.columns, fieldById],
  );
  const labelOf = (id: ViewColumnId) => (CUSTOM_COLUMN.test(id) ? (fieldById.get(id.slice(3))?.name ?? "") : COLUMN_LABELS[id as TableColumnId]);
  const widthOf = useCallback(
    (id: ViewColumnId) => state.widths[id] ?? DEFAULT_COLUMN_WIDTHS[id as TableColumnId] ?? 160,
    [state.widths],
  );

  /* While a header edge is being dragged the width is local, so the table
     follows the pointer without rewriting the URL on every move. */
  const [live, setLive] = useState<{ id: ViewColumnId; width: number } | null>(null);
  const liveWidth = (id: ViewColumnId) => (live?.id === id ? live.width : widthOf(id));
  const template = columns.map((id) => `${liveWidth(id)}px`).join(" ");
  const totalWidth = columns.reduce((sum, id) => sum + liveWidth(id), 0);

  const rows = useMemo(() => sortTasks(tasks, state.sort, customFields), [tasks, state.sort, customFields]);

  /* People, for the owner dropdown — fetched only if somebody can edit. */
  const [people, setPeople] = useState<Person[] | null>(null);
  const anyEditable = useMemo(() => tasks.some(canEdit), [tasks, canEdit]);
  const needsPeople = anyEditable || customFields.some((f) => f.type === "person");
  useEffect(() => {
    if (!needsPeople || people) return;
    let cancelled = false;
    api
      .get<{ people: Person[] }>("/people")
      .then((res) => !cancelled && setPeople(res.people))
      .catch(() => !cancelled && setPeople([]));
    return () => {
      cancelled = true;
    };
  }, [needsPeople, people]);

  /* ── virtual window ── */
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(600);
  const virtual = rows.length > VIRTUALISE_AFTER;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    setViewport(el.clientHeight || 600);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setViewport(el.clientHeight || 600));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const frame = useRef<number | null>(null);
  const onScroll = () => {
    if (!virtual || frame.current !== null) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      setScrollTop(scrollRef.current?.scrollTop ?? 0);
    });
  };
  const win = virtual
    ? virtualWindow(scrollTop, viewport, ROW_HEIGHT, rows.length)
    : { start: 0, end: rows.length, padTop: 0, padBottom: 0 };

  /* ── resizing ── */
  const beginResize = (event: ReactPointerEvent<HTMLSpanElement>, id: ViewColumnId) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = widthOf(id);
    let latest = startWidth;
    const move = (e: PointerEvent) => {
      latest = clampWidth(startWidth + e.clientX - startX);
      setLive({ id, width: latest });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setLive(null);
      if (latest !== startWidth) onState({ widths: { ...state.widths, [id]: latest } });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const resizeByKey = (event: React.KeyboardEvent, id: ViewColumnId) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const next = clampWidth(widthOf(id) + (event.key === "ArrowRight" ? 20 : -20));
    onState({ widths: { ...state.widths, [id]: next } });
  };

  /* ── column menu ── */
  const [columnsOpen, setColumnsOpen] = useState(false);
  const toggleColumn = (id: ViewColumnId) => {
    if (id === "title") return;
    const shown = columns.includes(id);
    // Keep the canonical column order whichever way the set changes; field columns follow in field order.
    const all: ViewColumnId[] = [...TABLE_COLUMN_IDS, ...customFields.map((f) => customColumnId(f.id))];
    const next = all.filter((c) => (c === id ? !shown : columns.includes(c)));
    onState({ columns: next });
  };

  return (
    <div className="tv-table-wrap">
      <div className="tv-toolbar">
        <div className="tv-popover">
          <button
            type="button"
            className="tv-btn"
            aria-expanded={columnsOpen}
            onClick={() => setColumnsOpen((v) => !v)}
          >
            <Icon name="settings" size={13} /> Columns
          </button>
          {columnsOpen && (
            <div className="tv-popover__panel" role="group" aria-label="Show or hide columns">
              {TABLE_COLUMN_IDS.map((id) => (
                <Checkbox
                  key={id}
                  label={COLUMN_LABELS[id]}
                  checked={columns.includes(id)}
                  disabled={id === "title"}
                  onChange={() => toggleColumn(id)}
                />
              ))}
              {customFields.map((f) => (
                <Checkbox
                  key={f.id}
                  label={f.name}
                  checked={columns.includes(customColumnId(f.id))}
                  onChange={() => toggleColumn(customColumnId(f.id))}
                />
              ))}
              <button
                type="button"
                className="tv-link"
                onClick={() => {
                  onState({ columns: [...TABLE_COLUMN_IDS].filter((c) => c !== "section" && c !== "milestone"), widths: {} });
                  setColumnsOpen(false);
                }}
              >
                Reset columns
              </button>
            </div>
          )}
        </div>
        <span className="tv-toolbar__count">
          {rows.length} {rows.length === 1 ? "task" : "tasks"}
        </span>
      </div>

      <div className="tv-table" ref={scrollRef} onScroll={onScroll} role="table" aria-label="Tasks" aria-rowcount={rows.length + 1}>
        <div className="tv-table__inner" style={{ width: totalWidth }}>
          <div className="tv-row tv-row--head" role="row" style={{ gridTemplateColumns: template }}>
            {columns.map((id) => {
              const sorted = state.sort?.key === id ? state.sort.dir : null;
              return (
                <div
                  key={id}
                  className="tv-cell tv-cell--head"
                  role="columnheader"
                  aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none"}
                >
                  <button
                    type="button"
                    className="tv-sort"
                    onClick={() => onState({ sort: nextSort(state.sort, id) })}
                  >
                    {labelOf(id)}
                    {sorted && <Icon name={sorted === "asc" ? "chevron-up" : "chevron-down"} size={12} />}
                  </button>
                  <span
                    className="tv-resize"
                    role="separator"
                    aria-orientation="vertical"
                    aria-valuenow={Math.round(liveWidth(id))}
                    aria-label={`Resize ${labelOf(id)} column`}
                    tabIndex={0}
                    onPointerDown={(e) => beginResize(e, id)}
                    onKeyDown={(e) => resizeByKey(e, id)}
                  />
                </div>
              );
            })}
          </div>

          <div style={{ paddingTop: win.padTop, paddingBottom: win.padBottom }}>
            {rows.slice(win.start, win.end).map((task) => (
              <TableRow
                key={task.id}
                task={task}
                columns={columns}
                template={template}
                editable={canEdit(task)}
                people={people}
                onPatch={onPatch}
                fieldById={fieldById}
                onCustomValue={onCustomValue}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface RowProps {
  task: Task;
  columns: ViewColumnId[];
  template: string;
  editable: boolean;
  people: Person[] | null;
  onPatch: TaskViewProps["onPatch"];
  fieldById: Map<string, CustomField>;
  onCustomValue?: TaskViewProps["onCustomValue"];
}

function TableRow({ task, columns, template, editable, people, onPatch, fieldById, onCustomValue }: RowProps) {
  const style: CSSProperties = { gridTemplateColumns: template, height: ROW_HEIGHT };
  return (
    <div className="tv-row" role="row" style={style}>
      {columns.map((id) => (
        <div key={id} className={`tv-cell tv-cell--${id.replace(":", "-")}`} role="cell">
          {CUSTOM_COLUMN.test(id) ? (
            <CustomCell
              field={fieldById.get(id.slice(3))!}
              task={task}
              editable={editable && !!onCustomValue}
              people={people}
              onCustomValue={onCustomValue}
            />
          ) : (
            <Cell id={id as TableColumnId} task={task} editable={editable} people={people} onPatch={onPatch} />
          )}
        </div>
      ))}
    </div>
  );
}

function Cell({
  id,
  task,
  editable,
  people,
  onPatch,
}: {
  id: TableColumnId;
  task: Task;
  editable: boolean;
  people: Person[] | null;
  onPatch: TaskViewProps["onPatch"];
}) {
  switch (id) {
    case "title":
      return (
        <Link to={`/work/task/${task.id}`} className={`tv-title ${task.status === "done" ? "is-done" : ""}`} title={task.title}>
          {task.title}
        </Link>
      );
    case "status":
      return editable ? (
        <select
          className="tv-select"
          aria-label={`Status of ${task.title}`}
          value={task.status}
          onChange={(e) => onPatch(task, { status: e.target.value as Task["status"] })}
        >
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      ) : (
        <Pill tone="neutral">{STATUSES.find((s) => s.value === task.status)?.label ?? task.status}</Pill>
      );
    case "priority":
      return editable ? (
        <select
          className="tv-select"
          aria-label={`Priority of ${task.title}`}
          value={task.priority}
          onChange={(e) => onPatch(task, { priority: e.target.value as Task["priority"] })}
        >
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {p[0]!.toUpperCase() + p.slice(1)}
            </option>
          ))}
        </select>
      ) : (
        <Pill tone={PRIORITY_TONE[task.priority]}>{task.priority}</Pill>
      );
    case "owners": {
      const owners = task.assignees ?? [];
      // Several owners cannot be expressed by picking one name, and replacing
      // them from here would silently drop the others, so that stays on the task.
      if (editable && owners.length <= 1) {
        const current = owners[0]?.userId ?? task.assigneeId ?? "";
        const options = people ?? [];
        const known = options.some((p) => p.id === current);
        return (
          <select
            className="tv-select"
            aria-label={`Owner of ${task.title}`}
            value={current}
            disabled={!people}
            onChange={(e) => e.target.value && onPatch(task, { assigneeId: e.target.value })}
          >
            {!current && <option value="">Unassigned</option>}
            {current && !known && <option value={current}>{owners[0]?.displayName ?? "Current owner"}</option>}
            {options.map((p) => (
              <option key={p.id} value={p.id}>
                {p.displayName}
              </option>
            ))}
          </select>
        );
      }
      if (owners.length === 0) return <span className="tv-muted">Unassigned</span>;
      return (
        <span className="tv-owners" title={owners.map((o) => o.displayName).join(", ")}>
          {owners.slice(0, 3).map((o) => (
            <Avatar key={o.userId} name={o.displayName} src={o.avatarUrl ?? undefined} size={20} />
          ))}
          <span className="tv-owners__name">
            {owners[0]!.displayName}
            {owners.length > 1 ? ` +${owners.length - 1}` : ""}
          </span>
        </span>
      );
    }
    case "due":
    case "start": {
      const field = id === "due" ? "dueAt" : "startAt";
      const value = task[field];
      return editable ? (
        <input
          type="date"
          className="tv-date"
          aria-label={`${id === "due" ? "Due" : "Start"} date of ${task.title}`}
          value={dateValue(value)}
          onChange={(e) => onPatch(task, { [field]: dateIso(e.target.value) })}
        />
      ) : value ? (
        <span>{dateValue(value)}</span>
      ) : (
        <span className="tv-muted">—</span>
      );
    }
    case "project":
      return task.projectCode ? (
        <span title={task.projectName ?? undefined}>{task.projectCode}</span>
      ) : (
        <span className="tv-muted">—</span>
      );
    case "section":
      return <span>{task.sectionName ?? <span className="tv-muted">—</span>}</span>;
    case "milestone":
      return <span>{task.milestoneName ?? <span className="tv-muted">—</span>}</span>;
    case "tags":
      return (
        <span className="tv-tags">
          {(task.tags ?? []).slice(0, 3).map((tag) => (
            <TagChip key={tag.id} tag={tag} />
          ))}
        </span>
      );
    case "checklist": {
      const total = task.checklistTotal ?? 0;
      if (!total) return <span className="tv-muted">—</span>;
      const done = task.checklistDone ?? 0;
      return (
        <span className="tv-progress" title={`${done} of ${total} done`}>
          <span className="tv-progress__bar">
            <span style={{ width: `${Math.round((done / total) * 100)}%` }} />
          </span>
          {done}/{total}
        </span>
      );
    }
  }
}

/**
 * One custom field value, edited in place. Text and number commit on blur or
 * Enter (not per keystroke, so a half-typed number is never saved); dropdown,
 * date and person commit on change. A rejected save puts the old value back.
 */
function CustomCell({
  field,
  task,
  editable,
  people,
  onCustomValue,
}: {
  field: CustomField;
  task: Task;
  editable: boolean;
  people: Person[] | null;
  onCustomValue?: TaskViewProps["onCustomValue"];
}) {
  const value = task.customValues?.[field.id];
  const shown = value === undefined ? "" : String(value);
  const [draft, setDraft] = useState(shown);
  useEffect(() => setDraft(shown), [shown]);
  const names = useMemo(() => new Map((people ?? []).map((p) => [p.id, p.displayName])), [people]);
  const label = `${field.name} of ${task.title}`;

  if (!editable || !onCustomValue) {
    const text = formatCustomValue(field, value, names);
    return text ? <span title={text}>{text}</span> : <span className="tv-muted">—</span>;
  }
  const save = (next: string | number | null) => {
    void onCustomValue(task, field.id, next).catch(() => setDraft(shown));
  };
  switch (field.type) {
    case "dropdown":
      return (
        <select className="tv-select" aria-label={label} value={shown} onChange={(e) => save(e.target.value || null)}>
          <option value="">—</option>
          {field.options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      );
    case "person": {
      const known = (people ?? []).some((p) => p.id === shown);
      return (
        <select className="tv-select" aria-label={label} value={shown} disabled={!people} onChange={(e) => save(e.target.value || null)}>
          <option value="">—</option>
          {shown && !known && <option value={shown}>{names.get(shown) ?? "Unknown person"}</option>}
          {(people ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.displayName}
            </option>
          ))}
        </select>
      );
    }
    case "date":
      return <input type="date" className="tv-date" aria-label={label} value={shown} onChange={(e) => save(e.target.value || null)} />;
    default: {
      const commit = () => {
        if (draft === shown) return;
        if (field.type === "number") {
          const n = parseNumberInput(draft);
          if (n === undefined) return setDraft(shown);
          return save(n);
        }
        save(draft.trim() === "" ? null : draft);
      };
      return (
        <input
          className="tv-input"
          aria-label={label}
          inputMode={field.type === "number" ? "decimal" : undefined}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") setDraft(shown);
          }}
        />
      );
    }
  }
}
